<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { TERRAIN_PRESETS, MIN_MAP_SIZE, MAX_MAP_SIZE } from '@/formats/types';

const emit = defineEmits<{
    create: [width: number, height: number, terrain: number];
    cancel: [];
}>();

const width = ref(100);
const height = ref(100);
const terrain = ref(0);

const presetSizes = [
    { label: 'Small (50x50)', width: 50, height: 50 },
    { label: 'Medium (100x100)', width: 100, height: 100 },
    { label: 'Large (200x200)', width: 200, height: 200 },
    { label: 'Very Large (400x400)', width: 400, height: 400 },
];

function applyPreset(preset: { width: number; height: number }): void {
    width.value = preset.width;
    height.value = preset.height;
}

// The inputs can hold anything: empty, fractional, out of range
function clampSize(value: unknown): number {
    const size = Math.round(Number(value));
    if (!Number.isFinite(size)) return MIN_MAP_SIZE;
    return Math.max(MIN_MAP_SIZE, Math.min(MAX_MAP_SIZE, size));
}

function handleCreate(): void {
    emit('create', clampSize(width.value), clampSize(height.value), terrain.value);
}

function handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
        emit('cancel');
    } else if (event.key === 'Enter' && (event.target as HTMLElement).tagName !== 'BUTTON') {
        // A focused button handles Enter itself
        handleCreate();
    }
}

onMounted(() => window.addEventListener('keydown', handleKeydown));
onUnmounted(() => window.removeEventListener('keydown', handleKeydown));

function handleBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('dialog-backdrop')) {
        emit('cancel');
    }
}
</script>

<template>
    <div class="dialog-backdrop" @click="handleBackdropClick">
        <div class="dialog">
            <div class="dialog-header">
                <h2>New Map</h2>
                <button class="close-btn" @click="emit('cancel')">&times;</button>
            </div>

            <div class="dialog-content">
                <!-- Presets -->
                <div class="form-section">
                    <label class="form-label">Quick Presets</label>
                    <div class="preset-buttons">
                        <button
                            v-for="preset in presetSizes"
                            :key="preset.label"
                            class="preset-btn"
                            @click="applyPreset(preset)"
                        >
                            {{ preset.label }}
                        </button>
                    </div>
                </div>

                <!-- Custom size -->
                <div class="form-section">
                    <label class="form-label">Map Size</label>
                    <div class="size-inputs">
                        <div class="input-group">
                            <label>Width</label>
                            <input
                                type="number"
                                v-model.number="width"
                                :min="MIN_MAP_SIZE"
                                :max="MAX_MAP_SIZE"
                            />
                        </div>
                        <span class="size-separator">&times;</span>
                        <div class="input-group">
                            <label>Height</label>
                            <input
                                type="number"
                                v-model.number="height"
                                :min="MIN_MAP_SIZE"
                                :max="MAX_MAP_SIZE"
                            />
                        </div>
                    </div>
                </div>

                <!-- Terrain -->
                <div class="form-section">
                    <label class="form-label">Base Terrain</label>
                    <div class="terrain-grid">
                        <button
                            v-for="(preset, index) in TERRAIN_PRESETS"
                            :key="preset.name"
                            class="terrain-btn"
                            :class="{ selected: terrain === index }"
                            @click="terrain = index"
                        >
                            {{ preset.name }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="dialog-footer">
                <button class="btn btn-secondary" @click="emit('cancel')">Cancel</button>
                <button class="btn btn-primary" @click="handleCreate">Create Map</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.dialog-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.dialog {
    background: var(--bg-panel);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    width: 400px;
    max-width: 90vw;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border-color);
}

.dialog-header h2 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
}

.close-btn {
    background: transparent;
    border: none;
    color: var(--text-secondary);
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.15s ease;
}

.close-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
}

.dialog-content {
    padding: 20px;
}

.form-section {
    margin-bottom: 20px;
}

.form-section:last-child {
    margin-bottom: 0;
}

.form-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-secondary);
    margin-bottom: 8px;
}

.preset-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.preset-btn {
    padding: 6px 12px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.preset-btn:hover {
    background: var(--accent-color);
    border-color: var(--accent-color);
    color: white;
}

.size-inputs {
    display: flex;
    align-items: flex-end;
    gap: 12px;
}

.input-group {
    flex: 1;
}

.input-group label {
    display: block;
    font-size: 11px;
    color: var(--text-secondary);
    margin-bottom: 4px;
}

.input-group input {
    width: 100%;
    padding: 8px 12px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    color: var(--text-primary);
    font-size: 14px;
}

.input-group input:focus {
    outline: none;
    border-color: var(--accent-color);
}

.size-separator {
    color: var(--text-secondary);
    padding-bottom: 8px;
}

.terrain-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.terrain-btn {
    padding: 10px;
    background: var(--bg-tertiary);
    border: 2px solid var(--border-color);
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.terrain-btn:hover {
    border-color: var(--accent-hover);
    color: var(--text-primary);
}

.terrain-btn.selected {
    border-color: var(--accent-color);
    background: var(--accent-color);
    color: white;
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 20px;
    border-top: 1px solid var(--border-color);
}

.btn {
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
}

.btn-secondary {
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
}

.btn-secondary:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
}

.btn-primary {
    background: var(--accent-color);
    border: 1px solid var(--accent-color);
    color: white;
}

.btn-primary:hover {
    background: var(--accent-hover);
    border-color: var(--accent-hover);
}
</style>
